// components/useMusic.ts
// [내 음악] 상태와 명령. 곡 파일은 USB 로 보드에 직접 저장하고(musicUpload.ts), 재생·정지·볼륨·삭제·목록은 MQTT 로 보낸다.
// 보드 펌웨어는 music.ino. 곡 번호(station)는 1부터이고 최대 6곡이다.
import { useCallback, useEffect, useRef, useState } from 'react';
import MQTTService from './MQTTService';
import { isSerialSupported, MUSIC_PUT_ERROR_TEXT, uploadMusicViaUsb } from './musicUpload';

export const MUSIC_MAX_SONGS = 6;
export const MUSIC_VOLUME_MAX = 21;
const MUSIC_VOLUME_SEND_GAP_MS = 250; // 슬라이더를 끄는 동안 볼륨 명령 사이의 최소 간격

export interface MusicSong {
  sn: number;      // 곡 번호 (1부터)
  title: string;
  sizeKb: number;
}

const MUSIC_ERROR_TEXT: Record<string, string> = {
  ...MUSIC_PUT_ERROR_TEXT,
  no_song: '그 번호의 곡이 보드에 없습니다.',
  play_fail: '곡을 재생하지 못했습니다. 파일이 mp3 가 맞는지 확인하세요.',
  no_psram: '재생을 시작하지 못했습니다. Arduino IDE 의 Tools → PSRAM 을 "OPI PSRAM" 으로 설정하고 다시 업로드하세요.',
  bad_action: '보드가 모르는 명령입니다. 펌웨어를 최신으로 설치하세요.',
};

// 이 브라우저에서 저장한 전체 제목. (보드의 목록 응답은 메시지 크기 때문에 제목을 20바이트까지만 담는다)
const titleKey = (mac: string, sn: number) => `music_title_${mac}_${sn}`;
function rememberTitle(mac: string, sn: number, sizeKb: number, title: string) {
  try { localStorage.setItem(titleKey(mac, sn), JSON.stringify({ sizeKb, title })); } catch (e) { /* 저장소를 못 쓰면 건너뛴다 */ }
}
function recalledTitle(mac: string, sn: number, sizeKb: number, boardTitle: string): string {
  try {
    const v = JSON.parse(localStorage.getItem(titleKey(mac, sn)) || 'null');
    // 크기가 같고 보드가 준 제목이 그 앞부분이면 같은 곡이다
    if (v && v.sizeKb === sizeKb && typeof v.title === 'string' && v.title.startsWith(boardTitle)) return v.title;
  } catch (e) { /* 무시 */ }
  return boardTitle;
}

// 보드의 list 응답 tx: 곡을 탭으로 나누고 "번호|KB|제목"
function parseList(text: string, mac: string): MusicSong[] {
  const songs: MusicSong[] = [];
  for (const row of text.split('\t')) {
    if (!row) continue;
    const parts = row.split('|');
    const sn = Number(parts[0]);
    const sizeKb = Number(parts[1]);
    if (!sn || Number.isNaN(sizeKb)) continue;
    const boardTitle = parts.slice(2).join('|');
    songs.push({ sn, sizeKb, title: recalledTitle(mac, sn, sizeKb, boardTitle) || `곡 ${sn}` });
  }
  return songs.sort((a, b) => a.sn - b.sn);
}

export function useMusic(mac: string, email: string, mqttMessage?: string) {
  const [songs, setSongs] = useState<MusicSong[]>([]);
  const [loaded, setLoaded] = useState(false);    // 보드에서 곡 목록을 한 번이라도 받았는지
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);      // 지금(또는 마지막) 곡 번호
  const [volume, setVolume] = useState(12);
  const [freeKb, setFreeKb] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [progress, setProgress] = useState(0);    // 0 ~ 100
  const volSentAt = useRef(0);
  const volTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshTimers = useRef<Array<ReturnType<typeof setTimeout>>>([]);

  const publishMusic = useCallback((action: string, extra: Record<string, any> = {}) => {
    if (!mac || !email) return;
    MQTTService.getInstance().publish(
      `i2r/${email}/in`,
      JSON.stringify({ command: 'music', mac, email, action, ...extra })
    );
  }, [mac, email]);

  const refresh = useCallback(() => publishMusic('list'), [publishMusic]);

  // 이 화면에 들어오면 보드의 곡 목록과 상태를 한 번 물어본다.
  useEffect(() => {
    refresh();
    return () => {
      if (volTimer.current) clearTimeout(volTimer.current);
      refreshTimers.current.forEach(clearTimeout);
      refreshTimers.current = [];
    };
  }, [refresh]);

  // 보드가 보내는 내 음악(mu) 응답을 받는다.
  useEffect(() => {
    if (!mqttMessage) return;
    try {
      const m = JSON.parse(mqttMessage);
      if (m.mac !== mac || m.command !== 'music') return;
      if (m.ok === 0 || m.ok === false) {
        if (m.playing !== undefined) setPlaying(m.playing === 1);
        setError(MUSIC_ERROR_TEXT[m.err] || `내 음악 오류 (${m.err || '알 수 없음'})`);
        return;
      }
      if (m.playing !== undefined) setPlaying(m.playing === 1);
      if (typeof m.station === 'number') setCurrent(m.station);
      if (typeof m.freeKb === 'number') setFreeKb(m.freeKb);
      // 볼륨 명령의 응답은 우리가 보낸 값의 되돌림이라 무시한다. (슬라이더를 끄는 중에 옛 값으로 튀지 않게)
      if (typeof m.volume === 'number' && m.action !== 'vol') setVolume(m.volume);
      if (m.action === 'list') { setSongs(parseList(String(m.text || ''), mac)); setLoaded(true); }
      else if (m.action === 'del') refresh();
    } catch (_) { /* 무시 */ }
  }, [mqttMessage, mac, refresh]);

  const play = useCallback((sn: number) => {
    setError('');
    setCurrent(sn);
    setPlaying(true); // 보드의 응답이 오면 실제 상태로 맞춘다
    publishMusic('play', { station: sn });
  }, [publishMusic]);

  const stop = useCallback(() => {
    setPlaying(false);
    publishMusic('stop');
  }, [publishMusic]);

  const remove = useCallback((sn: number) => {
    setError('');
    if (current === sn) setPlaying(false);
    publishMusic('del', { station: sn });
  }, [current, publishMusic]);

  const changeVolume = useCallback((v: number) => {
    setVolume(v);
    const send = () => {
      volSentAt.current = Date.now();
      volTimer.current = null;
      publishMusic('vol', { volume: v });
    };
    if (volTimer.current) clearTimeout(volTimer.current);
    const wait = Math.max(0, MUSIC_VOLUME_SEND_GAP_MS - (Date.now() - volSentAt.current));
    if (wait === 0) send();
    else volTimer.current = setTimeout(send, wait);
  }, [publishMusic]);

  // mp3 를 USB 로 보드에 저장한다.
  const upload = useCallback(async (file: File, title: string) => {
    setError('');
    setUploading(true);
    setProgress(0);
    try {
      const sn = await uploadMusicViaUsb(file, title, {
        onStatus: setUploadStatus,
        onProgress: (sent, total) => setProgress(Math.round((sent * 100) / total)),
      });
      rememberTitle(mac, sn, Math.ceil(file.size / 1024), title);
      setUploadStatus(`저장 완료: ${sn}번 곡. (보드가 다시 시작하면 잠시 후 목록에 나타납니다)`);
      // 보드가 다시 시작하거나 MQTT 가 다시 붙는 데 시간이 걸리므로 몇 번 더 물어본다.
      [3000, 10000, 25000].forEach((ms) => {
        refreshTimers.current.push(setTimeout(refresh, ms));
      });
    } catch (err: any) {
      setUploadStatus('');
      setError(err && err.name === 'NotFoundError' ? '' : `저장하지 못했습니다: ${err?.message || err}`);
    } finally {
      setUploading(false);
    }
  }, [mac, refresh]);

  return {
    songs, loaded, playing, current, volume, freeKb, error, uploading, uploadStatus, progress,
    serialSupported: isSerialSupported(),
    play, stop, remove, changeVolume, upload, refresh,
  };
}

export type MusicState = ReturnType<typeof useMusic>;
